import React, { useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Receipt, Upload, Download, Trash2, Loader2, FileText, Plus, X, DollarSign, Package } from 'lucide-react';

const STATE_COLORS = {
  pendiente: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  pagada:    'bg-[#A3FF12]/15 text-[#A3FF12] border-[#A3FF12]/40',
  anulada:   'bg-red-500/15 text-red-300 border-red-500/40',
};

const humanSize = (b) => (!b && b !== 0) ? '—' : b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/(1024*1024)).toFixed(1)} MB`;
const money = (n) => n == null ? '—' : `${Number(n).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €`;

const Facturacion = () => {
  const { user } = useAuth();
  const canWrite = user && (user.role === 'admin' || user.role === 'gestor');
  const [tab, setTab] = useState('invoices'); // invoices | preciarios
  const [invoices, setInvoices] = useState([]);
  const [preciarios, setPreciarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pendiente');
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showPreciarioForm, setShowPreciarioForm] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [i, p] = await Promise.all([
        api.get('/invoices'),
        api.get('/preciarios'),
      ]);
      setInvoices(i.data.items || []);
      setPreciarios(p.data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const filtered = filter === 'todas' ? invoices : invoices.filter((i) => i.state === filter);
  const totals = {
    pendiente: invoices.filter((i) => i.state === 'pendiente').reduce((a, b) => a + (b.amount || 0), 0),
    pagada:    invoices.filter((i) => i.state === 'pagada').reduce((a, b) => a + (b.amount || 0), 0),
  };

  const download = async (endpoint, filename) => {
    const r = await api.get(endpoint, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const changeInvoiceState = async (id, state) => {
    try {
      await api.patch(`/invoices/${id}`, { state });
      loadAll();
      toast.success('Estado actualizado');
    } catch { toast.error('Error'); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('¿Eliminar esta factura?')) return;
    await api.delete(`/invoices/${id}`);
    loadAll();
    toast.success('Factura eliminada');
  };

  const deletePreciario = async (id) => {
    if (!window.confirm('¿Eliminar este preciario?')) return;
    await api.delete(`/preciarios/${id}`);
    loadAll();
    toast.success('Preciario eliminado');
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="facturacion-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-widest text-[#A3FF12]">FACTURACIÓN</div>
            <h1 className="text-3xl sm:text-4xl font-black mt-1">Facturas y preciarios</h1>
            <p className="text-white/50 text-sm mt-2">
              Sube facturas pendientes, márcalas como pagadas y consulta los preciarios activos.
            </p>
          </div>
          <div className="flex gap-3">
            <StatCard label="Pendiente" value={money(totals.pendiente)} accent="orange"/>
            <StatCard label="Pagada" value={money(totals.pagada)} accent="green"/>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <TabButton active={tab === 'invoices'} onClick={() => setTab('invoices')} icon={Receipt} label={`Facturas (${invoices.length})`} testid="tab-invoices"/>
          <TabButton active={tab === 'preciarios'} onClick={() => setTab('preciarios')} icon={Package} label={`Preciarios (${preciarios.length})`} testid="tab-preciarios"/>
        </div>

        {tab === 'invoices' && (
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {['pendiente', 'pagada', 'anulada', 'todas'].map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${filter === f ? 'bg-[#A3FF12]/20 text-[#A3FF12] border-[#A3FF12]/40' : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'}`}
                  data-testid={`filter-${f}`}>
                  {f}
                </button>
              ))}
              {canWrite && (
                <button onClick={() => setShowInvoiceForm(!showInvoiceForm)}
                  className="ml-auto text-[11px] font-bold uppercase inline-flex items-center gap-1 bg-[#A3FF12] text-black hover:bg-[#b8ff3a] px-3 py-1.5 rounded-full"
                  data-testid="btn-new-invoice">
                  <Plus size={12}/> Nueva factura
                </button>
              )}
              <button onClick={async () => {
                const params = filter === 'todas' ? {} : { state: filter };
                const r = await api.get('/invoices/export.csv', { params, responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a'); a.href = url; a.download = `facturas-${filter}.csv`; a.click();
                URL.revokeObjectURL(url);
              }} className="text-[11px] font-bold uppercase inline-flex items-center gap-1 border border-white/10 hover:bg-white/5 text-white/70 px-3 py-1.5 rounded-full" data-testid="btn-export-invoices">
                <Download size={12}/> CSV
              </button>
            </div>

            {showInvoiceForm && <InvoiceForm onDone={() => { setShowInvoiceForm(false); loadAll(); }} onClose={() => setShowInvoiceForm(false)}/>}

            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#A3FF12]" size={24}/></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-white/40">Sin facturas en este estado.</div>
            ) : (
              <div className="space-y-2">
                {filtered.map((inv) => (
                  <div key={inv.id} className="bg-black/40 border border-white/10 rounded-xl p-3 flex items-center gap-3 flex-wrap sm:flex-nowrap" data-testid={`invoice-${inv.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATE_COLORS[inv.state]}`}>{inv.state_label || inv.state}</span>
                        <span className="text-white font-bold text-sm">{inv.supplier}</span>
                        {inv.invoice_number && <span className="text-white/40 text-xs font-mono">#{inv.invoice_number}</span>}
                      </div>
                      <div className="text-white/60 text-xs mt-0.5">{inv.concept || '—'}</div>
                      <div className="text-white/40 text-[10px] mt-0.5">
                        {inv.issue_date && <>Emitida {inv.issue_date} · </>}
                        {inv.due_date && <>Vence {inv.due_date} · </>}
                        Subido por {inv.uploaded_by_name} · {humanSize(inv.size_bytes)}
                      </div>
                    </div>
                    <div className="text-lg font-black text-white text-right flex-shrink-0">{money(inv.amount)}</div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => download(`/invoices/${inv.id}/download`, inv.file_name)}
                        className="text-white/60 hover:text-[#A3FF12] p-1.5" title="Descargar"><Download size={14}/></button>
                      {canWrite && inv.state !== 'pagada' && (
                        <button onClick={() => changeInvoiceState(inv.id, 'pagada')}
                          className="text-[#A3FF12] hover:bg-[#A3FF12]/10 border border-[#A3FF12]/30 rounded px-2 py-1 text-[10px] font-bold" title="Marcar pagada">✓ Pagar</button>
                      )}
                      {canWrite && (
                        <button onClick={() => deleteInvoice(inv.id)} className="text-white/40 hover:text-red-400 p-1.5" title="Eliminar"><Trash2 size={14}/></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'preciarios' && (
          <div>
            <div className="flex justify-end gap-2 mb-3">
              <button onClick={async () => {
                const r = await api.get('/preciarios/export.csv', { responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a'); a.href = url; a.download = 'preciarios.csv'; a.click();
                URL.revokeObjectURL(url);
              }} className="text-[11px] font-bold uppercase inline-flex items-center gap-1 border border-white/10 hover:bg-white/5 text-white/70 px-3 py-1.5 rounded-full" data-testid="btn-export-preciarios">
                <Download size={12}/> CSV
              </button>
              {canWrite && (
                <button onClick={() => setShowPreciarioForm(!showPreciarioForm)}
                  className="text-[11px] font-bold uppercase inline-flex items-center gap-1 bg-[#A3FF12] text-black hover:bg-[#b8ff3a] px-3 py-1.5 rounded-full"
                  data-testid="btn-new-preciario">
                  <Plus size={12}/> Nuevo preciario
                </button>
              )}
            </div>
            {showPreciarioForm && <PreciarioForm onDone={() => { setShowPreciarioForm(false); loadAll(); }} onClose={() => setShowPreciarioForm(false)}/>}
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#A3FF12]" size={24}/></div>
            ) : preciarios.length === 0 ? (
              <div className="text-center py-12 text-white/40">Aún no hay preciarios cargados.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {preciarios.map((p) => (
                  <div key={p.id} className="bg-black/40 border border-white/10 rounded-xl p-4" data-testid={`preciario-${p.id}`}>
                    <div className="flex items-start gap-2">
                      <FileText className="text-[#A3FF12] flex-shrink-0" size={18}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold text-sm">{p.name}</div>
                        {p.supplier && <div className="text-white/50 text-xs">{p.supplier}{p.year ? ` · ${p.year}` : ''}</div>}
                        {p.notes && <div className="text-white/40 text-xs mt-1">{p.notes}</div>}
                        <div className="text-white/40 text-[10px] mt-2">{humanSize(p.size_bytes)} · Subido por {p.uploaded_by_name}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => download(`/preciarios/${p.id}/download`, p.file_name)}
                        className="inline-flex items-center gap-1 text-xs bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] px-3 py-1 rounded-full font-bold hover:bg-[#A3FF12]/20">
                        <Download size={12}/> Descargar
                      </button>
                      {canWrite && (
                        <button onClick={() => deletePreciario(p.id)} className="text-white/40 hover:text-red-400 text-xs px-2 py-1"><Trash2 size={12}/></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm border ${active ? 'bg-[#A3FF12]/10 text-[#A3FF12] border-[#A3FF12]/40' : 'bg-black/30 text-white/60 border-white/10 hover:bg-white/5'}`}>
    <Icon size={14}/> {label}
  </button>
);

const StatCard = ({ label, value, accent }) => {
  const cls = accent === 'orange' ? 'text-orange-300 border-orange-500/30 bg-orange-500/10' : 'text-[#A3FF12] border-[#A3FF12]/30 bg-[#A3FF12]/10';
  return (
    <div className={`border rounded-xl px-4 py-2 ${cls}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
};

const InvoiceForm = ({ onDone, onClose }) => {
  const [supplier, setSupplier] = useState('');
  const [concept, setConcept] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!supplier || !file) { toast.error('Proveedor y archivo son obligatorios'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('supplier', supplier); fd.append('concept', concept);
    fd.append('invoice_number', invoiceNumber);
    if (amount) fd.append('amount', amount);
    if (issueDate) fd.append('issue_date', issueDate);
    if (dueDate) fd.append('due_date', dueDate);
    fd.append('state', 'pendiente');
    try {
      await api.post('/invoices', fd, { headers: {'Content-Type': 'multipart/form-data'}});
      toast.success('Factura subida');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error subiendo'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-black/60 border border-[#A3FF12]/30 rounded-xl p-4 mb-4 space-y-2" data-testid="invoice-form">
      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold text-sm">Nueva factura</h4>
        <button onClick={onClose} className="text-white/40 hover:text-white"><X size={16}/></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Proveedor *" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
        <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Nº factura" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" step="0.01" placeholder="Importe (€)" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
        <input value={issueDate} onChange={(e) => setIssueDate(e.target.value)} type="date" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
        <input value={dueDate} onChange={(e) => setDueDate(e.target.value)} type="date" placeholder="Vencimiento" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
        <input onChange={(e) => setFile(e.target.files?.[0])} type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white/60"/>
      </div>
      <textarea value={concept} onChange={(e) => setConcept(e.target.value)} placeholder="Concepto" rows={2} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
      <button onClick={submit} disabled={busy} className="bg-[#A3FF12] text-black py-2 px-4 rounded-lg text-sm font-bold hover:bg-[#b8ff3a] disabled:opacity-60 inline-flex items-center gap-2">
        {busy ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>} Subir factura
      </button>
    </div>
  );
};

const PreciarioForm = ({ onDone, onClose }) => {
  const [name, setName] = useState('');
  const [supplier, setSupplier] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name || !file) { toast.error('Nombre y archivo son obligatorios'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('name', name); fd.append('supplier', supplier);
    if (year) fd.append('year', year);
    fd.append('notes', notes);
    try {
      await api.post('/preciarios', fd, { headers: {'Content-Type': 'multipart/form-data'}});
      toast.success('Preciario subido');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error subiendo'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-black/60 border border-[#A3FF12]/30 rounded-xl p-4 mb-4 space-y-2" data-testid="preciario-form">
      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold text-sm">Nuevo preciario</h4>
        <button onClick={onClose} className="text-white/40 hover:text-white"><X size={16}/></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre * (ej. Preciario Cellnex 2026)" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none sm:col-span-2"/>
        <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Proveedor" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
        <input value={year} onChange={(e) => setYear(e.target.value)} type="number" placeholder="Año" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
        <input onChange={(e) => setFile(e.target.files?.[0])} type="file" accept=".pdf,.xlsx,.docx" className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white/60 sm:col-span-2"/>
      </div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas" rows={2} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
      <button onClick={submit} disabled={busy} className="bg-[#A3FF12] text-black py-2 px-4 rounded-lg text-sm font-bold hover:bg-[#b8ff3a] disabled:opacity-60 inline-flex items-center gap-2">
        {busy ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>} Subir preciario
      </button>
    </div>
  );
};

export default Facturacion;
